import { useState } from "react"
import { observer } from "mobx-react-lite"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { useAccount, useConfig } from "wagmi"

import type { ILock } from "@/domains/lock"
import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import SuperVoterAccessDialog from "@/components/super-voter/super-voter-access-dialog"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MessageBox } from "@/domains/common/components/message-box"
import { SwitchCardView } from "@/domains/common/components/switches/switch-card-view"
import { SwitchListView } from "@/domains/common/components/switches/switch-list-view"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { ScaleCard400 } from "@/domains/design/components/scale-card-400"
import { LockIcon, PlusCircleIcon, RevoteIcon } from "@/domains/icons"
import { maxLockMultipleInteraction, revoteMultipleNftInteraction } from "@/domains/interactions"
import LockTable from "@/domains/lock-page/components/lock-table"
import { TableProcessor } from "@/domains/table-processor"
import { MAX_NFTS_PER_WALLET } from "@/features/lock"
import NftCard from "@/features/lock/src/v5/nft-card"
import { UserLockStats } from "@/features/lock/src/v5/user-lock-stats"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useVestedToken } from "@/hooks/use-vested-token"
import useEventEmitter from "@/hooks/useEventEmitter"

export type LockStoreWithStats = ILock & {
  influence: number
}

export default observer(() => {
  const config = useConfig()
  const navigate = useNavigate()
  const account = useAccount()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const vestedToken = useVestedToken()
  const governanceToken = useGovernanceToken()
  const scaleApp = useScaleApplication()

  const [isSuperVoterDialogOpen, setIsSuperVoterDialogOpen] = useState(false)

  if (!scaleApp) {
    return null
  }

  const hasSuperVoterAccess = scaleApp.Pages.Vote.Store.hasSuperVoterAccess
  const { sortBy } = scaleApp.Pages.Lock

  const locks = scaleApp.Lock.Repository.getAll()
  const hasLocks = locks.length > 0
  const veTotalSupply = scaleApp.Stats.veTotalSupply

  //  this is duplicated from the lock table, lets see if we can use a single process..
  const processed = TableProcessor({
    rows: locks,
    sort: sortBy,
  })

  const onMaxLockMultipleClick = async (nftIds: Array<number>) => {
    try {
      const selected = processed.rows.filter((nft: LockStoreWithStats) => nftIds.includes(nft.id))

      // sanity check - are they trying to max lock an expired NFT?
      const expiredNfts = selected.filter((nft: LockStoreWithStats) => nft.hasExpired)
      if (expiredNfts.length > 0) {
        emitter.emit("error-max-locking", {
          title: "Max lock failed",
          description: `One or more of the selected NFTs has expired, please deselect and try again.`,
          variant: "error",
        })
        return
      }

      // sanity check - Are they trying to max lock a max locked NFT?
      const maxLockedNfts = selected.filter((nft: LockStoreWithStats) => nft.isMaxLocked)
      if (maxLockedNfts.length > 0) {
        emitter.emit("error-max-locking", {
          title: "Max lock failed",
          description: `One or more of the selected NFTs is already max locked, please deselect and try again.`,
          variant: "error",
        })
        return
      }

      if (!account.address) {
        emitter.emit("error-max-locking", {
          title: "Max lock failed",
          description: `Account not available, do you have a wallet connected?`,
          variant: "error",
        })
        return
      }

      const result = await maxLockMultipleInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        superVoterAddress: chainConfig.equalizer.contracts.superVoter,
        nftIds,
        emitter,
        eventBus: scaleApp.EventBus,
      })

      // Note: Manual refresh is no longer needed - LockDurationMultiExtendedEvent will trigger automatic refresh
      // But keeping it as a fallback in case event system fails
      if (result.success) {
        scaleApp.Lock.Orchestrator.query.forceExecute()
      }
      console.log("LockPage→onMaxLockClick→result", result)
    } catch (error) {
      console.error("LockPage→onMaxLockClick→error", error)
    }
  }

  const onRevoteMultipleClick = async (nftIds: Array<number>) => {
    try {
      const selected = processed.rows.filter((nft: LockStoreWithStats) => nftIds.includes(nft.id))

      // sanity check - are they trying to revote a voted NFT
      const votedNfts = selected.filter((nft: LockStoreWithStats) => nft.hasVotedThisEpoch)
      if (votedNfts.length > 0) {
        emitter.emit("error-revoting", {
          title: "Re-vote failed",
          description: `One or more of the selected NFTs has voted this epoch, please deselect and try again.\n NFTs: ${votedNfts
            .map((nft: LockStoreWithStats) => nft.id)
            .join(", ")}`,
          variant: "error",
        })
        return
      }

      if (!account.address) {
        emitter.emit("error-re-voting", {
          title: "Re-vote failed",
          description: `Account not available, do you have a wallet connected?`,
          variant: "error",
        })
        return
      }

      const result = await revoteMultipleNftInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        superVoterAddress: chainConfig.equalizer.contracts.superVoter,
        nftIds,
        emitter,
        maxLock: false,
      })

      if (result.success) {
        scaleApp.Lock.Orchestrator.query.forceExecute()
      }
      console.log("LockPage→onRevoteMultipleClick→result", result)
    } catch (error) {
      console.error("LockPage→onRevoteMultipleClick→error", error)
    }
  }

  const title = "Lock veSCALE"

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle
        title="Lock veSCALE"
        subTitle="More tokens locked for longer = greater voting power = higher rewards"
        middleEndItem={
          <div className="flex flex-row items-center gap-2">
            <ShowWhenTrue when={hasLocks}>
              <ScaleCard400 className="w-fit px-4 py-2">
                {!scaleApp.Pages.Lock.listView ? (
                  <SwitchCardView
                    checked={scaleApp.Pages.Lock.listView}
                    onCheckedChange={() => scaleApp.Pages.Lock.setListViewFromUi(true)}
                  />
                ) : (
                  <SwitchListView
                    checked={scaleApp.Pages.Lock.listView}
                    onCheckedChange={() => scaleApp.Pages.Lock.setListViewFromUi(false)}
                  />
                )}
              </ScaleCard400>
            </ShowWhenTrue>
            <ShowWhenTrue when={locks.length < MAX_NFTS_PER_WALLET}>
              <Button
                variant="primary"
                onClick={() => {
                  navigate("/lock/create")
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <PlusCircleIcon className="h-4 w-4" />
                  <div>Create lock</div>
                </div>
              </Button>
            </ShowWhenTrue>
            <ShowWhenTrue when={locks.length >= MAX_NFTS_PER_WALLET}>
              <Button variant="primary" disabled={true}>
                <div className="flex items-center justify-center gap-2">
                  <PlusCircleIcon className="h-4 w-4 opacity-50" />
                  <div>Max locks reached</div>
                </div>
              </Button>
            </ShowWhenTrue>
          </div>
        }
      />
      <SuperVoterAccessDialog isOpen={isSuperVoterDialogOpen} setIsOpen={setIsSuperVoterDialogOpen} />

      <ShowWhenTrue when={hasLocks}>
        <UserLockStats nfts={locks} veTotalSupply={veTotalSupply} />
      </ShowWhenTrue>

      <ShowWhenTrue when={hasLocks}>
        <ShowWhenTrue when={scaleApp.Pages.Lock.listView}>
          <LockTable />
        </ShowWhenTrue>
        <ShowWhenTrue when={!scaleApp.Pages.Lock.listView}>
          <div className="nftCardView grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3 4xl:grid-cols-4">
            {locks.map((nft) => {
              return (
                <NftCard
                  key={nft.id}
                  nft={nft}
                  veTotalSupply={veTotalSupply}
                  onMaxLockMultipleClick={onMaxLockMultipleClick}
                />
              )
            })}
          </div>
        </ShowWhenTrue>
      </ShowWhenTrue>

      <ShowWhenTrue when={scaleApp.Pages.Lock.getCanShowPortal()}>
        {createPortal(
          <div className="fixed inset-x-0 bottom-16 z-50 flex justify-center  ">
            <div className="flex w-fit items-center gap-3 rounded-2xl border border-primary-600 bg-primary-900 p-4 text-t100 shadow-[0_4px_12px_hsla(0,0%,0%,0.12),0_0_0_4px_hsla(224,81%,57%,0.25)]">
              <h2 className="rounded-md border border-primary-700 bg-primary-700/50 px-3 py-1 text-sm text-t100">
                {scaleApp.Pages.Lock.selectedNfts.length} selected
              </h2>
              <h2 className="">
                <Button
                  variant="primary"
                  onClick={() => {
                    // if we have not granted access yet, we should show the SuperVoteAccessDialog and get that done
                    if (hasSuperVoterAccess === false) {
                      setIsSuperVoterDialogOpen(true)
                      return
                    }

                    void onRevoteMultipleClick(scaleApp.Pages.Lock.selectedNfts)
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <RevoteIcon className="h-[16px] w-[17px]" />
                    <div>Re-vote</div>
                  </div>
                </Button>
              </h2>
              <h2 className="">
                <Button
                  variant="primary"
                  onClick={() => {
                    // if we have not granted access yet, we should show the SuperVoteAccessDialog and get that done
                    if (hasSuperVoterAccess === false) {
                      setIsSuperVoterDialogOpen(true)
                      return
                    }

                    void onMaxLockMultipleClick(scaleApp.Pages.Lock.selectedNfts)
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <LockIcon className="h-4 w-4" />
                    <div>Max Lock</div>
                  </div>
                </Button>
              </h2>
            </div>
          </div>,
          document.body,
        )}
      </ShowWhenTrue>
      {/* Show when no NFTs in connected wallet */}
      <ShowWhenTrue when={!hasLocks}>
        <div className="flex min-h-[40vh] flex-col gap-4">
          <MessageBox variant="info" size="sm">
            <div className="">
              Lock some {governanceToken.symbol} into a {vestedToken.symbol} NFT to earn weekly revenue.
            </div>
          </MessageBox>
          <div className="flex flex-grow flex-col items-center justify-center gap-6">
            <Card.HeaderIconContainer>
              <LockIcon className="h-4 w-4 text-t200" />
            </Card.HeaderIconContainer>
            <div className="flex flex-col items-center justify-center">
              <Card.HeaderTitle>NFT Rewards</Card.HeaderTitle>
              <div className="text-xs text-t200">Lock now to get access to weekly Voting Rewards.</div>
            </div>
            <Button
              variant="primary"
              onClick={() => {
                navigate("/lock/create")
              }}
            >
              <div className="flex items-center justify-center gap-2">
                <div>
                  <PlusCircleIcon className="h-4 w-4" />
                </div>
                <div>Create lock</div>
              </div>
            </Button>
          </div>
        </div>
      </ShowWhenTrue>
      {/* </Card.Container> */}
    </PageContainer>
  )
})
